import React, { useState, useMemo, useCallback, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '@/store/appContext';
import api from '@/store/api';

import DashboardHeader from './DashboardHeader';
import GoalCard from './GoalCard';
import BottomNav from './BottomNav';
import PromoBanner from './PromoBanner';
import GoalCarousel from './GoalCarousel';
import FaqCarousel from './FaqCarousel';
import KcRenewalBanner from './KcRenewalBanner';
import LiquidationBanner from './LiquidationBanner';
import LoadingFallback from '@/modules/shared/LoadingFallback';
import {
  CATEGORY_ICONS, checkGoalInactive, computeExpectedGross,
} from './dashboardConstants';
import { BS_RATE_FALLBACK } from '@/lib/constants';
import { useProfileFocusRefresh, useActivationPolling } from './hooks/useDashboardSync';

// ── Retry wrapper (same as App.js) for chunk-loading resilience ──
function lazyRetry(factory, retries = 3) {
  return lazy(() => {
    let attempt = 0;
    const tryLoad = () =>
      factory().catch((err) => {
        attempt += 1;
        if (attempt >= retries) throw err;
        return new Promise((r) => setTimeout(r, 1000 * attempt)).then(tryLoad);
      });
    return tryLoad();
  });
}

// Lazy: heavy tabs / modals rendered only on user interaction
const EditGoalView        = lazyRetry(() => import('./EditGoalView'));
const PaymentsWizard      = lazyRetry(() => import(/* webpackPrefetch: true */ './PaymentsWizard'));
const GoalDetailView      = lazyRetry(() => import('./GoalDetailView'));
const ProfileTab          = lazyRetry(() => import('./ProfileTab'));
const ComingSoonTab       = lazyRetry(() => import('./ComingSoonTab'));
const EditConfirmModal    = lazyRetry(() => import('./EditGoalModals').then(m => ({ default: m.EditConfirmModal })));
const EditUpsellModal     = lazyRetry(() => import('./EditGoalModals').then(m => ({ default: m.EditUpsellModal })));
const InactiveGoalModal   = lazyRetry(() => import('./InactiveGoalModal'));
const GroupGoalInvitationModal = lazyRetry(() => import('./GroupGoalInvitationModal'));
const LiquidationJourney  = lazyRetry(() => import('./LiquidationJourney'));

export default function DashboardView() {
  const {
    user, setUser, plan, selectedCategory, savingsModality, savingsSubModality,
    categories, kyc, setCurrentStep, logout,
    goals: contextGoals, setIsAddingGoal, selectCategory,
    setPlan, setSavingsModality, setSavingsSubModality, setKompaClubChoice,
    goalEditHistory, updateGoal, planTemplates,
  } = useApp();

  const [activeTab, setActiveTab] = useState('inicio');
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editModalGoal, setEditModalGoal] = useState(null);
  const [inactiveModal, setInactiveModal] = useState(null);
  // Liquidation wizard: which liquidation is open (null = closed).
  const [activeLiquidationId, setActiveLiquidationId] = useState(null);
  // Quick-pay: goal id pre-injected into PaymentsWizard when the user
  // taps the "+" FAB from GoalDetailView. Reset to null whenever the
  // wizard finishes or the user leaves the pagos tab.
  const [quickPayGoalId, setQuickPayGoalId] = useState(null);
  // Pending group-goal invitations — the first entry triggers the modal
  // on first dashboard load. Dismissed invitations (accept/reject) are
  // popped so the next one surfaces without a page refresh.
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, [selectedGoal, activeTab, editingGoal]);

  // ── Proactive UI sync (Bloque 1) ──
  // Refresh /api/profile on mount + window focus/visibility so the user
  // object always mirrors the DB, even when the user closes the wizard
  // mid-flow or returns to the tab after a long absence.
  useProfileFocusRefresh(setUser);
  // Short-window polling for Kompa Club activation: while
  // membershipActive=false AND a pending_* activation row exists,
  // poll /api/profile every 10s up to 5 attempts. The ActivationCard
  // clears itself automatically when the webhook flips the flag.
  useActivationPolling(user, setUser);

  // Fetch pending invitations on mount. Silent degradation when the
  // endpoint returns 404/500 (preview environments without the migration).
  useEffect(() => {
    let cancelled = false;
    api('/api/group-invitations/pending')
      .then((rows) => { if (!cancelled && Array.isArray(rows)) setPendingInvites(rows); })
      .catch(() => { /* silent — modal simply won't trigger */ });
    return () => { cancelled = true; };
  }, []);

  // ── Goals: real from context OR seed from onboarding (preview) ──
  const goals = useMemo(() => {
    if (contextGoals.length > 0) return contextGoals;
    const cat = categories.find(c => c.id === selectedCategory);
    const isKC = savingsModality === 'club';
    const mainGoal = {
      id: 1,
      category: selectedCategory || 'motos',
      icon: CATEGORY_ICONS[selectedCategory] || 'Bike',
      label: cat?.label || 'Meta principal',
      totalAmount: plan.amount,
      savedAmount: 0,
      frequency: { diario: 'Diario', semanal: 'Semanal', quincenal: 'Quincenal', mensual: 'Mensual' }[plan.frequency] || 'Mensual',
      frequencyId: plan.frequency,
      months: plan.months,
      modality: savingsModality || 'total',
      subModality: savingsSubModality || 'individual',
      startDate: '01 Mar 2026',
      estimatedEnd: (() => {
        const d = new Date(2026, 2, 1);
        d.setMonth(d.getMonth() + plan.months);
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
      })(),
      payments: [],
      status: 'active',
      grossPaid: 0,
      planName: isKC ? 'Plan Acelerado' : 'Plan KOMPA+',
      planCode: isKC ? 'acelerado' : 'kompa_plus',
      bonus: isKC ? { hasBonus: false } : {
        hasBonus: true, bonusPct: 0.15,
        maxBonusEur: Math.round(plan.amount * 0.15 * 100) / 100,
        earnedBonusEur: 0,
        progressPct: 0,
      },
    };
    const extraGoal = {
      id: 2,
      category: 'navidades', icon: 'Gift', label: 'Navidades',
      totalAmount: 1500, savedAmount: 0,
      frequency: 'Quincenal', frequencyId: 'quincenal', months: 12,
      modality: 'total', subModality: 'individual',
      startDate: '15 Ene 2026', estimatedEnd: '15 Nov 2026',
      payments: [],
      status: 'active', grossPaid: 0,
      planName: 'Plan KOMPA+', planCode: 'kompa_plus',
      bonus: { hasBonus: true, bonusPct: 0.15, maxBonusEur: 225, earnedBonusEur: 0, progressPct: 0 },
    };
    return [mainGoal, extraGoal];
  }, [contextGoals, plan, selectedCategory, savingsModality, savingsSubModality, categories]);

  const activeGoals = useMemo(
    () => goals
      .filter(g => !checkGoalInactive(g))
      .map(g => ({ ...g, _expectedGross: computeExpectedGross(g, planTemplates) }))
      .sort((a, b) => b.totalAmount - a.totalAmount),
    [goals, planTemplates],
  );

  const sortedGoals = useMemo(() => {
    const active = [];
    const inactive = [];
    for (const g of goals) {
      const eg = computeExpectedGross(g, planTemplates);
      if (checkGoalInactive(g)) inactive.push({ ...g, _inactive: true, _expectedGross: eg });
      else active.push({ ...g, _inactive: false, _expectedGross: eg });
    }
    active.sort((a, b) => b.totalAmount - a.totalAmount);
    return [...active, ...inactive];
  }, [goals, planTemplates]);

  // El badge "KOMPA CLUB" refleja la suscripción REALMENTE activa, no el
  // simple hecho de tener una meta club. Un usuario que creó una meta club
  // pero nunca pagó el €6,99 (membership_active=false) NO es miembro y no
  // debe mostrar el badge — evita la confusión de soporte/usuario.
  const isVip = Boolean(user?.clubSubscriptionPaidThisMonth);

  // ── Handlers ──
  const handleTabChange = (tab) => {
    setSelectedGoal(null);
    if (tab !== 'pagos') setQuickPayGoalId(null);
    setActiveTab(tab);
  };

  // Quick-pay from GoalDetailView FAB → switch to pagos tab with the
  // selected goal pre-injected so the wizard skips the goal picker.
  const handleQuickPay = useCallback((goal) => {
    setQuickPayGoalId(goal.id);
    setSelectedGoal(null);
    setActiveTab('pagos');
  }, []);

  const handleAddAction = () => {
    selectCategory(null);
    setPlan({ amount: 0, frequency: 'mensual', months: 12 });
    setSavingsModality(null);
    setSavingsSubModality(null);
    setKompaClubChoice(null);
    setIsAddingGoal(true);
    setCurrentStep(2);
  };

  const handleEditGoal = (goal) => {
    const isAcelerado = goal.modality === 'club' || goal.subModality === 'grupal' || goal.planCode === 'acelerado';
    if (isAcelerado) {
      setEditingGoal(goal.id);
    } else if (goalEditHistory[goal.id]) {
      setEditModalGoal(goal);
      setEditModal('upsell');
    } else {
      setEditModalGoal(goal);
      setEditModal('confirm');
    }
  };

  const closeEditModal = () => { setEditModal(null); setEditModalGoal(null); };

  const handleConfirmEdit = () => {
    setEditingGoal(editModalGoal.id);
    closeEditModal();
  };

  const handleSaveGoalEdit = (goalId, updates) => {
    updateGoal(goalId, updates);
    setEditingGoal(null);
  };

  const handleGoalClick = useCallback((goal) => {
    if (goal._inactive) setInactiveModal(goal);
    else setSelectedGoal(goal.id);
  }, []);

  // ── Sub-views (edit / detail) ──
  if (editingGoal) {
    const goal = goals.find(g => g.id === editingGoal);
    if (!goal) { setEditingGoal(null); return null; }
    return (
      <>
        <Suspense fallback={<LoadingFallback label="Editando meta…" />}>
          <EditGoalView
            goal={goal}
            onSave={(updates) => handleSaveGoalEdit(goal.id, updates)}
            onBack={() => setEditingGoal(null)}
          />
        </Suspense>
        <BottomNav active={activeTab} onChange={handleTabChange} />
      </>
    );
  }

  if (selectedGoal) {
    const goal = goals.find(g => g.id === selectedGoal);
    if (!goal) { setSelectedGoal(null); return null; }
    return (
      <>
        <Suspense fallback={<LoadingFallback label="Cargando detalle…" />}>
          <GoalDetailView
            goal={goal}
            planTemplates={planTemplates}
            onBack={() => setSelectedGoal(null)}
            onQuickPay={handleQuickPay}
          />
        </Suspense>
        <BottomNav active={activeTab} onChange={handleTabChange} />
      </>
    );
  }

  // Liquidation journey (full-screen wizard)
  if (activeLiquidationId) {
    return (
      <>
        <Suspense fallback={<LoadingFallback label="Cargando beneficio…" />}>
          <LiquidationJourney
            liquidationId={activeLiquidationId}
            onBack={() => setActiveLiquidationId(null)}
          />
        </Suspense>
        <BottomNav active={activeTab} onChange={handleTabChange} />
      </>
    );
  }

  // ── Main tabbed view ──
  const clubSubscription = planTemplates.find(t => t.code === 'acelerado')?.subscriptionEur || 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pb-20" data-testid="dashboard-view">
      <DashboardHeader user={user} isVip={isVip} onAddGoal={activeTab === 'inicio' ? handleAddAction : null} />

      {activeTab === 'inicio' && <PromoBanner />}
      {activeTab === 'inicio' && (
        <LiquidationBanner onOpen={(id) => setActiveLiquidationId(id)} />
      )}
      {activeTab === 'inicio' && (
        <KcRenewalBanner onRenew={() => setActiveTab('pagos')} />
      )}
      {activeTab === 'inicio' && <GoalCarousel activeGoals={activeGoals} />}

      <div className="flex-1 px-6 md:px-8">
        <AnimatePresence mode="wait">
          {activeTab === 'inicio' && (
            <motion.div key="inicio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-black uppercase tracking-wider">Mis metas</h3>
                <span className="text-xs text-gray-400">{activeGoals.length} activas</span>
              </div>
              <div
                className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-6 px-6 md:-mx-8 md:px-8 pb-1"
                data-testid="mis-metas-row"
              >
                {sortedGoals.map((goal, i) => (
                  <div
                    key={goal.id}
                    onClick={() => handleGoalClick(goal)}
                    className="cursor-pointer flex-shrink-0 w-[82%] sm:w-[320px] snap-start"
                    data-testid={`goal-card-click-${goal.id}`}
                  >
                    <GoalCard
                      goal={goal}
                      index={i}
                      onEdit={goal._inactive ? undefined : handleEditGoal}
                      inactive={goal._inactive}
                      bsRate={BS_RATE_FALLBACK}
                      expectedGross={goal._expectedGross}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 -mx-6 md:-mx-8">
                <FaqCarousel />
              </div>
            </motion.div>
          )}

          {activeTab === 'pagos' && (
            <motion.div key="pagos" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Suspense fallback={<LoadingFallback label="Cargando pagos…" />}>
                <PaymentsWizard
                  goals={goals}
                  preselectedGoalId={quickPayGoalId}
                  onDone={() => { setQuickPayGoalId(null); setActiveTab('inicio'); }}
                />
              </Suspense>
            </motion.div>
          )}

          {(activeTab === 'juegos' || activeTab === 'aliados') && (
            <Suspense fallback={<LoadingFallback />}>
              <ComingSoonTab kind={activeTab} />
            </Suspense>
          )}

          {activeTab === 'perfil' && (
            <Suspense fallback={<LoadingFallback label="Cargando perfil…" />}>
              <ProfileTab
                user={user}
                setUser={setUser}
                kyc={kyc}
                onVerifyKyc={() => setCurrentStep(8)}
                onLogout={logout}
              />
            </Suspense>
          )}
        </AnimatePresence>
      </div>

      {editModal === 'confirm' && (
        <Suspense fallback={null}>
          <EditConfirmModal onConfirm={handleConfirmEdit} onClose={closeEditModal} />
        </Suspense>
      )}
      {editModal === 'upsell' && (
        <Suspense fallback={null}>
          <EditUpsellModal onClose={closeEditModal} subscriptionEur={clubSubscription} />
        </Suspense>
      )}
      {inactiveModal && (
        <Suspense fallback={null}>
          <InactiveGoalModal goal={inactiveModal} onClose={() => setInactiveModal(null)} />
        </Suspense>
      )}

      {pendingInvites.length > 0 && (
        <Suspense fallback={null}>
          <GroupGoalInvitationModal
            invitation={pendingInvites[0]}
            onClose={() => setPendingInvites(prev => prev.slice(1))}
          />
        </Suspense>
      )}

      <BottomNav active={activeTab} onChange={handleTabChange} />
    </div>
  );
}
